import {
  ActionType,
  EmptyListingButtonTypes,
  ExtraFilter,
  FilterAll,
  FilterComponent,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  ListingSelectOption,
  Operator,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useNavigation,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  EXAINFRA_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  GroupingKeys,
  ListingTestIds,
  MainTargetId,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
  ViewKeys,
} from "../../constants/uiConstants";
import { AzureSubscriptionSummary } from "../../gen/clients/mchub-azure-api-client";
import {
  CloudExadataInfrastructureSummary,
  CloudExadataInfrastructureSummaryCollection,
  CloudExadataInfrastructureSummaryStatusEnum,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import {
  getFieldCustomOptions,
  getStatusInfo,
  optimizedRetryOption,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFilterStatePills } from "../../hooks/useFilterStatePills";
import { useLocationFilters } from "../../hooks/useLocationFilters";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { ExaInfraDeleteProps, newExaInfraDelete } from "../../operations/ExaInfra/ExaInfraDelete";
import { chainFunctions, getAzureLocationName, getOciRegion } from "../../utils";

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.EXAINFRA_DETAILS].map(config => config.key);

interface ExaInfraItem {
  id: string;
  displayName: string;
  subscriptionName: string;
  subscriptionId: string;
  resourceGroupName: string;
  location: string;
  status?: CloudExadataInfrastructureSummaryStatusEnum;
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
  lifecycleDetails?: string;
  freeformTags?: { [key: string]: string; };
}

interface AzureSubscriptionSummaryMap {
  [key: string]: AzureSubscriptionSummary;
}

export enum ColumnIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  FreeformTags = "freeformTags",
}

export enum ColumnTestIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  FreeformTags = "freeformTags",
}

export enum ActionIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

export enum ActionBarTestIds {
  Create = "create",
  Refresh = "refresh",
  Delete = "delete"
}

export enum FilterTestIds {
  SubscriptionFilter = "subscription-filter",
  LocationFilter = "location-filter"
}

export const ExaInfraList = (): JSX.Element => {
  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { trigger: triggerDeleteExaInfra } = useOperation<ExaInfraDeleteProps>(newExaInfraDelete);
  const { closePanels } = useSidePanel();

  const { navigateTo } = useNavigation(ConsoleContext);
  // eslint-disable-next-line max-len
  const { preferredSubscription, locale } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);
  const { listId } = useParams();

  const [selection, setSelection] = React.useState<ExaInfraItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] = React.useState<string>("");
  const [subscriptionOptions, setSubscriptionOptions] = React.useState<{ key: string, text: string }[]>([]);

  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();

  const { locationOptions, selectedLocation, setSelectedLocation } = useLocationFilters(
    filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
  );

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const [filterComponentRef, setFilterComponentRef] = React.useState<FilterComponent>();
  const pillFilterValues = filterComponentRef?.getFilters();
  const filterTextValue = filterComponentRef?.getFilterText();
  const [filterText, setFilterText] = React.useState(filterState.filterTextValue || filterTextValue);

  const { perPageItemsCount } = useRowCount();

  const groupingOptions: ListingSelectOption[] = [
    {
      key: GroupingKeys.NoGrouping,
      text: Messages.listExaInfra.filters.noGrouping(),
    },
  ];

  const viewOptions: ViewSelectOption[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listExaInfra.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const getLocation = (value: ExaInfraItem): JSX.Element => (
    <span>{getAzureLocationName(value.location)}</span>
  );

  const getStatus = (value: ExaInfraItem): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.DisplayName,
      testId: ColumnTestIds.DisplayName,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.displayName}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXAINFRA_DETAILS][0].panelPath}?location=${value.location}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
          iconName={SvgIconIds.infraSvg}
        />
      ),
    },
    {
      itemProp: ColumnIds.SubscriptionName,
      testId: ColumnTestIds.SubscriptionName,
      name: Messages.labels.subscription(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureSubscriptionLink resourceId={value.id} subscriptionName={value.subscriptionName} />,
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: ExaInfraItem,
        b: ExaInfraItem,
      ) => statusSortComparator<ExaInfraItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
  ];

  const {
    refresh: exaInfraRefresh,
    loading: exaInfraLoading,
    response: exaInfraResponse,
  } = useQueryCall({
    wait: !selectedSubscription || !selectedLocation,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).exaDatabaseApi.listCloudExadataInfrastructuresBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExadbInfra(),
      },
    },
  });

  // eslint-disable-next-line max-len
  const exaInfras = exaInfraResponse && responseItemstoArray<CloudExadataInfrastructureSummaryCollection, CloudExadataInfrastructureSummary>(exaInfraResponse);

  const items = React.useMemo(() => {
    const newExaInfras: ExaInfraItem[] = [];

    if (exaInfras && subscriptions && subscriptions.length > 0) {
      const subscriptionsMap = subscriptions.reduce((
        map: AzureSubscriptionSummaryMap,
        subscription: AzureSubscriptionSummary,
      ) => {
        map[subscription.id] = subscription;
        return map;
      }, {});
      exaInfras.forEach(exaInfra => {
        if (exaInfra.lifecycleState !== "TERMINATED") {
          const { subscriptionId, resourceGroup, resourceName } = parseId(exaInfra.id);
          const exaInfraSubscriptionName = subscriptionsMap && subscriptionsMap[subscriptionId].name;
          newExaInfras.push({
            id: exaInfra.id,
            displayName: exaInfra.name || resourceName,
            subscriptionName: exaInfraSubscriptionName,
            subscriptionId,
            resourceGroupName: resourceGroup,
            location: exaInfra.location,
            status: exaInfra.status,
            lifecycleDetails: exaInfra.lifecycleDetails,
            freeformTags: exaInfra.freeformTags,
          });
        }
      });
    }
    return newExaInfras;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exaInfras), JSON.stringify(subscriptions)]);

  const onRefresh = (): void => {
    exaInfraRefresh();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    if (selectedItems[0] !== selection[0]) {
      // TODO: remove once Listing component implements this pattern
      closePanels();
    }
    setSelection(selectedItems as ExaInfraItem[]);
  };

  const exaInfraActions: ActionType[] = [
    {
      key: ActionIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionIds.Create, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        navigateTo(EXAINFRA_CREATE_ROUTE, PageId.EXAINFRA_CREATE, {
          analytics: {
            pageId: PageId.EXA_LIST_HUB,
            panelId: DetailsPanelId.INFRASTRUCTURES,
            actionName: ActionIds.Create,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        chainFunctions(onRefresh, listingComponentRef?.resetTableSelection);
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: !selection.length,
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES);
        triggerDeleteExaInfra({
          targetId: MainTargetId,
          exaInfraId: selection[0].id,
          location: selectedLocation,
          onExecute: () => {
            listingComponentRef?.resetTableSelection();
            exaInfraRefresh();
          },
          onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.EXA_LIST_HUB, DetailsPanelId.INFRASTRUCTURES),
        });
      },
    },
  ];

  React.useEffect(() => {
    const newSubscriptionOptions: { key: string, text: string }[] = [];
    // eslint-disable-next-line no-unused-expressions
    subscriptions && subscriptions.forEach(subscription => {
      newSubscriptionOptions.push({ key: subscription.id, text: subscription.name });
    });

    setSubscriptionOptions(newSubscriptionOptions);
    if (filterState.defaultSubscription) {
      setSelectedSubscription(filterState.defaultSubscription);
      if (defaultExtraFilters) defaultExtraFilters[0].value = filterState.defaultSubscription;
    } else if (preferredSubscription) {
      setSelectedSubscription(preferredSubscription);
    } else {
      setSelectedSubscription(newSubscriptionOptions.length ? newSubscriptionOptions[0].key : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptions)]);

  const subscriptionFilter: ExtraFilter = {
    testId: FilterTestIds.SubscriptionFilter,
    name: columns[1].name,
    operator: Operator.Equal,
    value: filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
    fieldKey: columns[1].itemProp,
    customOptions: subscriptionOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedSubscription(value);
        filterState.setDefaultSubscription(value);
      }
    },
    hideDelete: true,
  };

  const resourceGroupFilter: ExtraFilter = {
    name: columns[2].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[2].itemProp)?.value || [FilterAll],
    fieldKey: columns[2].itemProp,
    customOptions: getFieldCustomOptions(items?.map(item => item.resourceGroupName), (fieldVal: string) => fieldVal),
  };

  React.useEffect(() => {
    if (filterState.defaultLocation) {
      setSelectedLocation(filterState.defaultLocation);
      if (defaultExtraFilters) defaultExtraFilters[1].value = filterState.defaultLocation;
    } else {
      setSelectedLocation(filterState.pillFilterStateValue.filterValues?.[1]?.value || selectedLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationFilter: ExtraFilter = {
    testId: FilterTestIds.LocationFilter,
    name: columns[3].name,
    operator: Operator.Equal,
    value: filterState.defaultLocation ? filterState.defaultLocation : selectedLocation,
    fieldKey: columns[3].itemProp,
    customOptions: locationOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedLocation(value);
        filterState.setDefaultLocation(value);
      }
    },
    hideDelete: true,
  };

  const lifecycleStateFilter: ExtraFilter = {
    name: columns[4].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[4].itemProp)?.value || [FilterAll],
    fieldKey: columns[4].itemProp,
    messageCallBack: (value: string) => stateT(value),
    customOptions: getFieldCustomOptions(items?.map(item => item.status || ""), (fieldVal: string) => stateT(fieldVal)),
  };

  const allFilterPills = [
    subscriptionFilter,
    locationFilter,
    resourceGroupFilter,
    lifecycleStateFilter,
  ];

  const filterPills: ExtraFilter[] = [
    subscriptionFilter,
    locationFilter,
  ];

  const textFilterCallBack = (rowItem: ExaInfraItem): ExaInfraItem => {
    const displayRowItem = { ...rowItem };
    displayRowItem.status = stateT(
      displayRowItem.status || "",
    ) as CloudExadataInfrastructureSummaryStatusEnum;
    displayRowItem.location = getAzureLocationName(displayRowItem.location);
    return displayRowItem;
  };

  React.useEffect(() => {
    setFilterText(filterTextValue);
    filterState.setFilterTextValue(filterTextValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTextValue]);

  const defaultExtraFilters = useFilterStatePills(pillFilterValues, allFilterPills, listId, filterPills);

  const resetFilterValues = (): void => {
    const defaultPillValues = filterPills.map(filter => ({ fieldKey: filter.fieldKey, value: filter.value as string }));
    filterState.setFilterStateValue({ filterValues: defaultPillValues, listId, defaultPills: filterPills });
    filterState.setFilterTextValue("");
    setFilterText("");
  };

  return (
    <Listing
      testId={ListingTestIds.ExaInfra}
      listingComponentRef={setListingComponentRef}
      items={items}
      listColumns={columns}
      itemKey={ColumnIds.DisplayName}
      itemsPerPage={perPageItemsCount}
      actionBarItems={exaInfraActions}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      emptyList={{
        title: Messages.listExaInfra.emptyList.title(),
        emptyButtons: [
          {
            type: EmptyListingButtonTypes.PRIMARY,
            text: Messages.actions.createExaInfra(),
            action: () => navigateTo(EXAINFRA_CREATE_ROUTE, PageId.EXAINFRA_CREATE),
          },
        ],
        watermark: SvgIconIds.infraWaterMarkSvg,
      }}
      isLoading={exaInfraLoading || subscriptionLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.DisplayName,
      }}
      groupingSelect={{
        groupingOptions,
        defaultSelectedKey: GroupingKeys.NoGrouping,
      }}
      viewSelect={{
        viewOptions,
        defaultSelectedKey: ViewKeys.ListView,
      }}
      filtering={{
        allExtraFilters: allFilterPills,
        defaultExtraFilters,
        defaultFilterText: filterText,
        textFilterCallBack,
        componentRef: setFilterComponentRef,
        onClearFilters: resetFilterValues,
      }}
    />
  );
};
